import {
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  IconButton,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import CircularLoading from "@saleor/autobooking/components/CircularLoading";
import { DateTimeUtility } from "@saleor/autobooking/utils/datetime";
import { useOrderLastRiderAssigned } from "@saleor/motherOrders/queries";
import { ASSIGNMENT_COOLDOWN_SECONDS } from "@saleor/motherOrders/utils/handlers";
import React from "react";

const useStyles = makeStyles(
  theme => ({
    btnGray: {
      "&:hover": {
        backgroundColor: theme.palette.grey[500]
      },
      backgroundColor: theme.palette.grey[500]
    },
    btnPrimary: {
      "&:hover": {
        backgroundColor: theme.palette.primary.main
      },
      backgroundColor: theme.palette.primary.main
    },
    buttonActionCenter: {
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
      marginBottom: 20,
      marginTop: 20
    },
    centerContent: {
      fontWeight: 500,
      padding: 10,
      textAlign: "center"
    },
    closeButton: {
      color: theme.palette.grey[500],
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1)
    },
    divFlexCentered: {
      display: "flex",
      justifyContent: "center",
      marginBottom: 20,
      marginTop: 50
    },
    marginRight20: {
      marginRight: "20px"
    },
    templateDialogTitle: {
      fontSize: 20,
      fontWeight: 700,
      marginLeft: 2
    },
    textWhite: {
      color: "#FFF",
      fontWeight: 700
    }
  }),
  { name: "RiderRefreshDialog" }
);

export interface RiderRefreshDialogProps {
  orderId: string | null;
  open: boolean;
  onClose: () => void;
}

const RiderRefreshDialog: React.FC<RiderRefreshDialogProps> = props => {
  const { orderId, open, onClose } = props;
  const classes = useStyles(props);

  const SECONDS_INTERVAL = 1000;
  const [timerInterval, setTimerInterval] = React.useState<NodeJS.Timeout>(
    null
  );
  const [timerTimeout, setTimerTimeout] = React.useState<NodeJS.Timeout>(null);
  const [secs, setSecs] = React.useState(null);

  const { data: riderData, refetch, loading } = useOrderLastRiderAssigned({
    skip: !orderId,
    variables: { id: orderId }
  });

  React.useEffect(() => {
    if (!open) {
      stopInterval();
      return;
    }
    refetch().then(() => {
      computeForRemainingSeconds();
    });
  }, [open]);

  const computeForRemainingSeconds = () => {
    if (!riderData?.order?.lastRiderAssignedAt) {
      return;
    }

    const targetDateTime = new Date(riderData?.order?.lastRiderAssignedAt);
    targetDateTime.setSeconds(
      targetDateTime.getSeconds() + ASSIGNMENT_COOLDOWN_SECONDS
    );

    const nowDateTime = new Date();
    const dateTimeDifference = targetDateTime.getTime() - nowDateTime.getTime();

    if (dateTimeDifference > 0) {
      let index = 0;
      let returnValue = getRemainingTimer(dateTimeDifference, index);
      index = 1;

      setTimerInterval(
        setInterval(() => {
          returnValue = getRemainingTimer(returnValue, index);
          index++;
        }, SECONDS_INTERVAL)
      );

      setTimerTimeout(
        setTimeout(() => {
          stopInterval(true);
        }, dateTimeDifference)
      );
    }
  };

  React.useEffect(() => {
    if (!open) {
      stopInterval();
      return;
    }
    computeForRemainingSeconds();
  }, [riderData?.order?.lastRiderAssignedAt]);

  const getRemainingTimer = (_timerCountdown, index) => {
    const dateTimeDifference =
      index > 0 ? _timerCountdown - SECONDS_INTERVAL : _timerCountdown;

    const dtObj = DateTimeUtility.getDateTimeComponents(dateTimeDifference);
    setSecs(dtObj.seconds);

    return dateTimeDifference;
  };

  const stopInterval = (callOnClose: boolean = false) => {
    if (timerInterval) {
      clearInterval(timerInterval);
    }
    if (timerTimeout) {
      clearTimeout(timerTimeout);
    }
    if (callOnClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      aria-labelledby="thirdparty-assign-cooldown-dialog-title"
      aria-describedby="thirdparty-assign-cooldown-dialog"
    >
      <DialogContent>
        {loading ? (
          <CircularLoading />
        ) : (
          <>
            <IconButton
              aria-label="closeIconButton"
              className={classes.closeButton}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
            <DialogContentText id="thirdparty-assign-cooldown-dialog">
              <div className={classes.divFlexCentered}>
                <Typography
                  className={classes.templateDialogTitle}
                  variant="h5"
                >
                  This order was recently updated.
                </Typography>
              </div>

              <div className={classes.centerContent}>
                You can resume once the cooldown ends.
              </div>

              <div className={classes.buttonActionCenter}>
                <Button
                  color="inherit"
                  variant="contained"
                  className={`${classes.marginRight20} ${classes.btnGray}`}
                  onClick={onClose}
                >
                  <Typography className={classes.textWhite}>DISMISS</Typography>
                </Button>
                <Button
                  color="inherit"
                  variant="contained"
                  className={classes.btnPrimary}
                  disabled={true}
                >
                  <Typography className={classes.textWhite}>
                    {`RESUME(${secs})`}
                  </Typography>
                </Button>
              </div>
            </DialogContentText>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RiderRefreshDialog;
